"use client";

import type { EditorBackend } from "@/lib/editor-backend/types";
import { useDemoStore } from "@/stores/demo-store";
import { extractTextFromTipTap } from "@/lib/ai/tiptap-extractor";
import { trackDemoImageUrl } from "@/lib/demo/demo-image-urls";
import type { DemoAssistantContext } from "@/lib/demo/demo-assistant";
import { markdownToTiptap } from "@/lib/markdown-utils";
import { CHAPTER_SAVE_CONFLICT_MESSAGE, getChapterContentHash } from "@/lib/editor/chapter-content-version";
import { jsonValuesEqual } from "@/lib/json-equality";

/**
 * Demo backend: satisfies the same EditorBackend contract as the real one, but
 * every operation runs against the in-browser demo store — no Server Actions,
 * no database, no network. This is what makes the `/try` editor feel real while
 * persisting nothing server-side.
 */
export const demoEditorBackend: EditorBackend = {
    isDemo: true,
    assistantStreamEndpoint: "/api/demo/assistant/stream",

    async saveChapterContent(chapterId, content, wordCount, options) {
        for (;;) {
            const store = useDemoStore.getState();
            const chapter = store.chapters.find((chapter) => chapter.id === chapterId);
            if (!chapter) return { error: "Chapter not found" };
            if (options?.autosave && chapter.status !== "draft") {
                return { error: "This chapter is no longer a draft. Save changes manually." };
            }
            if (options?.expectedContentHash !== undefined && !jsonValuesEqual(chapter.content, content)) {
                const currentHash = await getChapterContentHash(chapter.content);
                // Hashing yields to other saves/publishing. Re-read before the
                // synchronous store write, retrying if this chapter changed.
                if (useDemoStore.getState().chapters.find(item => item.id === chapterId) !== chapter) continue;
                if (currentHash !== options.expectedContentHash) {
                    return { error: CHAPTER_SAVE_CONFLICT_MESSAGE, conflict: true };
                }
            }
            store.saveChapter(chapterId, content, wordCount);
            return { success: true };
        }
    },

    async createChapter(_storyId, title) {
        const id = useDemoStore.getState().addChapter(title);
        return { id };
    },

    async createStoryVolume() {
        return { error: "Volumes are available in the full editor" };
    },

    async renameStoryVolume() {
        return { error: "Volumes are available in the full editor" };
    },

    async deleteStoryVolume() {
        return { error: "Volumes are available in the full editor" };
    },

    async deleteVolumeChapters() {
        return { error: "Volumes are available in the full editor" };
    },

    async updateStoryOutline() {
        return { error: "Volumes are available in the full editor" };
    },

    async updateStoryTitle(_storyId, title) {
        useDemoStore.getState().setStoryTitle(title);
        return { success: true };
    },

    async updateChapterMeta(chapterId, updates) {
        if (updates.status) {
            useDemoStore.getState().setChapterStatus(chapterId, updates.status);
        }
        if (updates.title !== undefined || updates.authorNote !== undefined) {
            useDemoStore.getState().setChapterMeta(chapterId, {
                title: updates.title,
                authorNote: updates.authorNote,
            });
        }
        return { success: true };
    },

    async deleteChapter() {
        return { error: "Chapter deletion is unavailable in the demo" };
    },

    async reorderChapters(_storyId, orderedIds) {
        useDemoStore.getState().reorderChapters(orderedIds);
        return { success: true };
    },

    async syncChapterMentions() {
        // No mention table in the demo — nothing to sync.
        return { success: true };
    },

    async searchWikiEntries(_storyId, query) {
        return useDemoStore
            .getState()
            .searchWiki(query)
            .map((l) => ({
                id: l.id,
                name: l.name,
                category: l.category,
                categoryLabel: l.categoryLabel,
            }));
    },

    async uploadChapterImage(_storyId, file) {
        // Demo persists nothing — hold the image in-memory via a blob URL. It's
        // tracked so `reset()` can revoke it rather than leak for the session.
        return { url: trackDemoImageUrl(URL.createObjectURL(file)) };
    },

    getDemoAssistantContext(): DemoAssistantContext {
        const s = useDemoStore.getState();
        return {
            storyTitle: s.storyTitle,
            activeChapterId: s.activeChapterId,
            chapters: s.chapters
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((c) => ({
                    id: c.id,
                    title: c.title,
                    sortOrder: c.sortOrder,
                    status: c.status,
                    text: extractTextFromTipTap(c.content),
                })),
            lore: s.lore.map((l) => ({
                id: l.id,
                name: l.name,
                category: l.category,
                categoryLabel: l.categoryLabel,
                description: l.description,
            })),
        };
    },

    applyDemoCreatedRecord(payload) {
        const store = useDemoStore.getState();
        const mutation = payload.demoMutation;
        if (!mutation) return;
        if (
            payload.recordKind === "loreConnection" &&
            mutation.kind === "loreConnection"
        ) {
            store.addLoreConnection(
                mutation.sourceEntryId,
                mutation.targetEntryId,
                mutation.relationLabel,
                payload.recordId,
            );
            return;
        }
        if (payload.recordKind !== "loreEntry" || mutation.kind !== "loreEntry") {
            return;
        }
        if (!mutation.name) return;
        const name = mutation.name;
        const parent = mutation.parentEntryId
            ? store.lore.find((entry) => entry.id === mutation.parentEntryId)
            : undefined;
        let category = parent
            ? store.loreCategories.find(
                  (candidate) => candidate.id === parent.categoryId,
              )
            : (store.loreCategories.find(
                  (candidate) => candidate.category === mutation.category,
              ) ??
              store.loreCategories.find(
                  (candidate) => candidate.category === "other",
              ) ??
              store.loreCategories[0]);
        if (!category) {
            const categoryId = store.addLoreCategory("other", "Other");
            category = useDemoStore
                .getState()
                .loreCategories.find((candidate) => candidate.id === categoryId);
        }
        if (!category) return;
        const id = store.addLoreEntry(
            category.id,
            name,
            parent?.id ?? null,
            payload.recordId,
        );
        if (id && (mutation.description || mutation.contentMarkdown)) {
            const content = mutation.contentMarkdown
                ? markdownToTiptap(mutation.contentMarkdown)
                : markdownToTiptap(mutation.description ?? "");
            useDemoStore.getState().updateLoreEntry(id, {
                description:
                    mutation.description ?? extractTextFromTipTap(content),
                content,
            });
        }
    },

    undoDemoCreatedRecord(payload) {
        if (payload.recordKind === "loreConnection") {
            const exists = useDemoStore
                .getState()
                .loreConnections.some(
                    (connection) => connection.id === payload.recordId,
                );
            if (!exists) return false;
            useDemoStore.getState().deleteLoreConnection(payload.recordId);
            return true;
        }
        if (payload.recordKind !== "loreEntry") return false;
        const exists = useDemoStore
            .getState()
            .lore.some((entry) => entry.id === payload.recordId);
        if (!exists) return false;
        useDemoStore.getState().deleteLoreEntry(payload.recordId);
        return true;
    },

    getDemoAuthToken(): string | undefined {
        // Cloudflare Turnstile tokens are single-use, so consume it: hand the
        // current token to this request and clear it. The widget observes the
        // clear and fetches a fresh token for the next request.
        const { turnstileToken, setTurnstileToken } = useDemoStore.getState();
        if (turnstileToken) setTurnstileToken(null);
        return turnstileToken ?? undefined;
    },
};
